/**
 * Brand Kits v1.5 — shared vocabulary.
 *
 * The sibling of `v1_5/Header/theme.ts` and `v1_5/Chat/theme.ts`, following the same contract: the
 * design system is light-only, so a dark-first component cannot read the global tokens directly.
 * Every part here takes a `theme` prop, applies `kitThemeClass`, and reads only `--kv15-*` /
 * `--hv15-*` custom properties. Light is a token swap, nothing else.
 *
 * The theme union is NOT redeclared. A second `'dark' | 'light'` is a second design system, so this
 * folder re-exports the Header's.
 */

import { cn } from '~/lib/utils';
import headerThemes from '../Header/HeaderTheme.module.css';
import kitThemes from './BrandKitsTheme.module.css';

export type { HeaderHover, HeaderTheme } from '../Header/theme';
import type { HeaderTheme } from '../Header/theme';

/**
 * Applies BOTH theme layers.
 *
 * Brand Kits reuses the header's `--hv15-*` ramp wholesale — text tones, hairlines, the two button
 * plates, motion curves and durations — rather than restating it under a second name. `--kv15-*`
 * adds only the grounds this surface introduces and the two values that have to sit on the user's
 * own colours.
 *
 * Every component's root does `className={cn(kitThemeClass(theme), styles.root, className)}`.
 * Custom properties inherit, so a themed composite themes every atom inside it for free.
 */
export function kitThemeClass(theme: HeaderTheme = 'dark'): string {
  return cn(headerThemes[theme], kitThemes[theme]);
}

/**
 * The six panels the kit detail switches between, in rail order.
 *
 * `tone` and `settings` have no artboard yet — they are in the union because the rail draws them
 * and a rail that could only name four of its six rows would be a rail with a hole in it. The
 * chassis takes whatever panel it is handed, so neither needs a component to exist.
 */
export type KitSection = 'overview' | 'logos' | 'colors' | 'typography' | 'compositions' | 'tone' | 'settings';

/** The two rows of a kit card's `⋯`. Both act on the whole kit, which is why neither is a control on the card itself. */
export type KitCardAction = 'make-default' | 'remove';

/**
 * What a tile in the kits grid IS. One component draws all three because the design draws them in
 * one grid at one size: they are the same 176px tile in three states, not three cards.
 *
 * - `kit` — a kit the user owns. Opens; carries a `⋯`; may be the default.
 * - `preset` — one of ours, not yet taken. The whole tile is the "add" button, so it carries a `+`
 *   permanently rather than a `⋯` on hover: the plus is what the tile MEANS, not a way in to more.
 * - `pending` — a site being read right now. No colours and no mark, because neither is known yet;
 *   the tile is painted at the page's own ground so it reads as the hole the kit will fill.
 */
export type KitCardKind = 'kit' | 'preset' | 'pending';

/**
 * One brand colour, as the panels draw it.
 *
 * `value` is any CSS colour the extractor read off the site. It is CONTENT: it is passed to the DOM
 * as a runtime custom property and never appears in a stylesheet.
 */
export interface KitColor {
  /** The role the kit assigns it — `Accent`, `Background`, `Text`. Printed verbatim on the chip. */
  role: string;
  /** The colour itself. Any CSS colour value. */
  value: string;
  /** Printed on the scrim, verbatim. `#C2410C`. Separate from `value` so a named or `oklch()` colour can still show a hex. */
  label: string;
  /**
   * The one colour of its group that generation leads with.
   *
   * Off the artboard: it exists because the shipping kit holds several colours per role and the
   * first of each group is the one every deck is built from, which is a fact about the palette that
   * has to be visible somewhere. Draws a second chip; absent, the tile is exactly what K7 draws.
   */
  primary?: boolean;
}

/** One drawing of the mark, and the ground it is meant to be read on. Both grounds are the user's. */
export interface KitLogoVariation {
  id: string;
  /** The chip, printed verbatim: `On dark`, `On light`, `Monochrome`. */
  name: string;
  /** File type, printed verbatim on the scrim: `SVG`, `PNG`. */
  format: string;
  /** The ground the mark is shown against. CONTENT — a runtime colour, never a token. */
  ground: string;
  /**
   * `src` for the mark itself. Omit and the tile draws NOTHING — the white square with a hole in it
   * that Paper puts here is the designer standing in for a real logo file, exactly as the coloured
   * square with an initial stands in for one on a kit card. It is not a state the panel has.
   */
  src?: string;
  /** This is the variation the kit actually uses. Draws the 2px ring. */
  selected?: boolean;
  /** A file is being stored for this variation right now, or it is being removed. */
  busy?: boolean;
  /**
   * The name is the user's rather than ours, so the chip becomes a field.
   *
   * `Light mode` and `Dark mode` are built in and cannot be renamed; a variation the user added
   * carries whatever they called it. Needs `KitLogosPanelProps.onRenameVariation` to do anything.
   */
  editableName?: boolean;
  /**
   * The ground is the user's choice, so the tile offers the control that changes it.
   *
   * Needs `KitLogosPanelProps.onVariationGround`. Off on the two built-ins, whose grounds are what
   * "light mode" and "dark mode" MEAN.
   */
  groundEditable?: boolean;
}

/** One row of the typography panel: what it is called, what it is set in, and a line set in it. */
export interface KitTypeRole {
  /** `Display`, `Heading`, `Body`. */
  name: string;
  /** The meta line, printed verbatim: `Söhne · 30`. The middle dot is U+00B7 with a space either side, as everywhere in v1.5. */
  meta: string;
  /** The specimen sentence. */
  sample: string;
  /** Rendered size in px. A runtime value — the kit's scale, not the app's. */
  size: number;
  /** The family to set the specimen in. CONTENT: falls back to the app's display face when the kit's is not loadable. */
  family?: string;
  /**
   * Letter-spacing, in em, and uppercase — the two other things a role can BE that a name and a
   * size do not say.
   *
   * Both are drawn on the specimen because both are visible in it: a display role tracked at -0.02
   * looks like a different typeface from the same face at 0. Leading is deliberately NOT here — the
   * row's fixed 30px line box is the whole reason five samples from 12 to 30 can share a height.
   */
  tracking?: number;
  uppercase?: boolean;

  /* ── the editable half ─────────────────────────────────────────────────────────────────────
   *
   * None of this is on K8, which draws a scale that has already been decided. It is here because
   * the shipping kit's scale is edited on this page and nowhere else, and every one of these is
   * inert until `KitTypographyPanelProps.onRoleChange` is supplied.
   */

  /** The stored size, in SLIDE pixels. `size` is what the specimen is drawn at; this is the value. */
  storedSize?: number;
  /** The role's CSS weight, 100–900. */
  weight?: number;
  /** The sentence telling generation when to reach for this role. */
  usage?: string;
}
